export function CapRateValuation() {
  return (
    <section className="py-20 px-4 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Quick Valuation Method: Cap Rate Analysis
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            The capitalization rate (cap rate) is one of the most important metrics in commercial real estate. Understanding your market's cap rate allows you to quickly estimate your property's value based on its income production.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">What Is a Cap Rate?</h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              The cap rate is the ratio of a property's Net Operating Income (NOI) to its purchase price or current market value, expressed as a percentage. It represents the expected annual return on investment if you purchased the property with all cash.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              The formula works two ways: <strong>Cap Rate = NOI ÷ Property Value</strong> (to find the cap rate), or <strong>Property Value = NOI ÷ Cap Rate</strong> (to find the value). This inverse relationship means lower cap rates indicate higher property values and vice versa.
            </p>
            <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-r-lg">
              <h4 className="font-bold text-gray-900 mb-2 text-lg">Key Formulas:</h4>
              <p className="text-gray-800 text-lg font-semibold mb-1">Cap Rate = NOI ÷ Property Value</p>
              <p className="text-gray-800 text-lg font-semibold mb-2">Property Value = NOI ÷ Cap Rate</p>
              <p className="text-gray-600">The fundamental metric for income property valuation</p>
            </div>
          </div>
          <div>
            <img
              src="https://public.readdy.ai/ai/img_res/87d02db8e71fbc481fa99f18391e8ea1.jpg"
              alt="Cap rate calculation and analysis for apartment buildings"
              className="w-full h-96 object-cover object-top rounded-lg shadow-lg"
            />
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-8 lg:p-12 mb-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">Understanding Cap Rate Ranges</h3>
          <p className="text-lg text-gray-700 mb-10 text-center max-w-4xl mx-auto leading-relaxed">
            Cap rates vary significantly based on location, property quality, market conditions, and perceived risk. Here's what different cap rate ranges typically indicate:
          </p>
          
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            <div className="bg-white rounded-lg p-6 shadow-md border-t-4 border-green-500">
              <div className="text-center mb-4">
                <div className="text-4xl font-bold text-green-600 mb-2">4-6%</div>
                <h4 className="text-xl font-bold text-gray-900">Low Cap Rate</h4>
              </div>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 mt-1"></i>
                  <span>Prime locations (major metros)</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 mt-1"></i>
                  <span>Class A properties</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 mt-1"></i>
                  <span>Newer construction</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 mt-1"></i>
                  <span>Strong tenant base</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 mt-1"></i>
                  <span>Lower risk, higher prices</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-md border-t-4 border-yellow-500">
              <div className="text-center mb-4">
                <div className="text-4xl font-bold text-yellow-600 mb-2">6-8%</div>
                <h4 className="text-xl font-bold text-gray-900">Moderate Cap Rate</h4>
              </div>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-yellow-600 mt-1"></i>
                  <span>Secondary markets</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-yellow-600 mt-1"></i>
                  <span>Class B properties</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-yellow-600 mt-1"></i>
                  <span>Moderate age/condition</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-yellow-600 mt-1"></i>
                  <span>Stable occupancy</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-yellow-600 mt-1"></i>
                  <span>Balanced risk/return</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-md border-t-4 border-red-500">
              <div className="text-center mb-4">
                <div className="text-4xl font-bold text-red-600 mb-2">8-10%+</div>
                <h4 className="text-xl font-bold text-gray-900">High Cap Rate</h4>
              </div>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-red-600 mt-1"></i>
                  <span>Tertiary/rural markets</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-red-600 mt-1"></i>
                  <span>Class C/D properties</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-red-600 mt-1"></i>
                  <span>Older buildings</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-red-600 mt-1"></i>
                  <span>Higher vacancy risk</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-red-600 mt-1"></i>
                  <span>Higher risk, lower prices</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-teal-600 text-white rounded-lg p-6 max-w-4xl mx-auto">
            <h4 className="text-xl font-bold mb-3 text-center">Key Insight: The Cap Rate Inverse Relationship</h4>
            <p className="text-lg leading-relaxed text-center">
              Lower cap rates mean HIGHER property values (investors accept lower returns for safer investments). Higher cap rates mean LOWER property values (investors demand higher returns to compensate for increased risk). Understanding this inverse relationship is crucial for valuation.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <img
              src="https://readdy.ai/api/search-image?query=real-estate-market-research-cap-rate-trends-market-analysis-investment-data-comparable-properties-market-conditions-financial-research-professional-investor-analyzing-market-reports-charts-graphs&width=800&height=600&seq=market-cap-rate-research-001&orientation=landscape"
              alt="Researching market cap rates for property valuation"
              className="w-full h-96 object-cover object-top rounded-lg shadow-lg"
            />
          </div>
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">How to Find Your Market Cap Rate</h3>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              To use cap rate for valuation, you need to know the current market cap rate for properties similar to yours. Here are the best ways to determine your local market cap rate:
            </p>
            <div className="space-y-4">
              <div className="bg-white rounded-lg p-5 shadow-md border-l-4 border-teal-600">
                <h4 className="font-bold text-gray-900 mb-2">Analyze Recent Sales</h4>
                <p className="text-gray-600">Find 3-5 comparable sales, obtain their NOI, and calculate: Cap Rate = NOI ÷ Sale Price</p>
              </div>
              <div className="bg-white rounded-lg p-5 shadow-md border-l-4 border-teal-600">
                <h4 className="font-bold text-gray-900 mb-2">Talk to Local Brokers</h4>
                <p className="text-gray-600">Commercial real estate brokers track cap rates and can provide current market data for your area</p>
              </div>
              <div className="bg-white rounded-lg p-5 shadow-md border-l-4 border-teal-600">
                <h4 className="font-bold text-gray-900 mb-2">Review Market Reports</h4>
                <p className="text-gray-600">CBRE, Marcus & Millichap, and other firms publish quarterly cap rate surveys by market</p>
              </div>
              <div className="bg-white rounded-lg p-5 shadow-md border-l-4 border-teal-600">
                <h4 className="font-bold text-gray-900 mb-2">Consult Appraisers</h4>
                <p className="text-gray-600">Professional appraisers maintain databases of cap rates and can provide accurate market data</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-lg p-8 lg:p-12 text-white">
          <h3 className="text-3xl font-bold mb-8 text-center">Cap Rate Valuation Examples</h3>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            <div className="bg-white/10 rounded-lg p-6">
              <h4 className="text-xl font-bold mb-4 text-teal-400">Example 1: Class A Property</h4>
              <div className="space-y-3 text-lg">
                <p><strong>Property Details:</strong></p>
                <p>• Location: Prime urban area</p>
                <p>• Net Operating Income: $180,000</p>
                <p>• Market Cap Rate: 5.5%</p>
                <div className="border-t border-white/30 pt-3 mt-3">
                  <p className="text-xl font-bold">Calculation:</p>
                  <p>$180,000 ÷ 0.055 = <span className="text-2xl text-teal-400">$3,272,727</span></p>
                  <p className="text-base opacity-90 mt-2">Lower cap rate = Higher value</p>
                </div>
              </div>
            </div>

            <div className="bg-white/10 rounded-lg p-6">
              <h4 className="text-xl font-bold mb-4 text-yellow-400">Example 2: Class B Property</h4>
              <div className="space-y-3 text-lg">
                <p><strong>Property Details:</strong></p>
                <p>• Location: Suburban area</p>
                <p>• Net Operating Income: $180,000</p>
                <p>• Market Cap Rate: 7.0%</p>
                <div className="border-t border-white/30 pt-3 mt-3">
                  <p className="text-xl font-bold">Calculation:</p>
                  <p>$180,000 ÷ 0.070 = <span className="text-2xl text-yellow-400">$2,571,429</span></p>
                  <p className="text-base opacity-90 mt-2">Moderate cap rate = Moderate value</p>
                </div>
              </div>
            </div>

            <div className="bg-white/10 rounded-lg p-6">
              <h4 className="text-xl font-bold mb-4 text-red-400">Example 3: Class C Property</h4>
              <div className="space-y-3 text-lg">
                <p><strong>Property Details:</strong></p>
                <p>• Location: Secondary market</p>
                <p>• Net Operating Income: $180,000</p>
                <p>• Market Cap Rate: 9.0%</p>
                <div className="border-t border-white/30 pt-3 mt-3">
                  <p className="text-xl font-bold">Calculation:</p>
                  <p>$180,000 ÷ 0.090 = <span className="text-2xl text-red-400">$2,000,000</span></p>
                  <p className="text-base opacity-90 mt-2">Higher cap rate = Lower value</p>
                </div>
              </div>
            </div>

            <div className="bg-white/10 rounded-lg p-6">
              <h4 className="text-xl font-bold mb-4 text-orange-400">Example 4: Value-Add Property</h4>
              <div className="space-y-3 text-lg">
                <p><strong>Property Details:</strong></p>
                <p>• Location: Emerging neighborhood</p>
                <p>• Net Operating Income: $180,000</p>
                <p>• Market Cap Rate: 8.0%</p>
                <div className="border-t border-white/30 pt-3 mt-3">
                  <p className="text-xl font-bold">Calculation:</p>
                  <p>$180,000 ÷ 0.080 = <span className="text-2xl text-orange-400">$2,250,000</span></p>
                  <p className="text-base opacity-90 mt-2">Opportunity for value increase</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 bg-white/10 rounded-lg p-6 max-w-5xl mx-auto">
            <p className="text-lg text-center leading-relaxed">
              <strong>Notice:</strong> All four properties generate the same $180,000 NOI, but their values range from $2,000,000 to $3,272,727 based solely on cap rate differences. This demonstrates why understanding your market cap rate is essential for accurate valuation.
            </p>
          </div>
        </div>

        <div className="mt-16 bg-teal-50 border-l-4 border-teal-600 p-8 rounded-r-lg">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0">
              <i className="ri-lightbulb-line text-teal-600 text-2xl"></i>
            </div>
            <div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Using Cap Rate for Quick Valuations</h4>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                Once you know your property's NOI and your market's cap rate, you can instantly estimate value. This makes cap rate analysis one of the fastest and most reliable quick valuation methods for apartment buildings.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                <strong>Pro Tip:</strong> Cap rates change with market conditions. In strong markets, cap rates compress (go down), increasing property values. In weak markets, cap rates expand (go up), decreasing values. Always use current market cap rates, not historical data.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}